import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import {
  setCart,
  fetchCartProducts,
  setLoader,
  createEmptyCart,
} from "../../actions";
import Product from "../Product";
import { useProductById, useSeller } from "../../hooks/useAmplifyData";

const ProductPage = () => {
  const dispatch = useDispatch();
  const { id: productId } = useParams();
  const auth = useSelector(state => state.auth);
  const loader = useSelector(state => state.loader);

  // Fetch product using Amplify Gen 2
  const {
    data: product,
    isLoading: isLoadingProduct,
    error: productError,
  } = useProductById(parseInt(productId));

  // Fetch seller once we have the product
  const sellerId = product?.seller_id ?? 0;
  const {
    data: seller,
    isLoading: isLoadingSeller,
  } = useSeller(sellerId);

  const isLoading = isLoadingProduct || isLoadingSeller;

  useEffect(() => {
    if (auth.is_signed_in) {
      dispatch(createEmptyCart(() => {
        dispatch(fetchCartProducts());
      }));
    }
  }, [auth.is_signed_in, dispatch]);

  // Show/hide loader based on loading state
  useEffect(() => {
    if (isLoading) {
      dispatch(setLoader(true, "Fetching Product..."));
    } else {
      dispatch(setLoader(false, null));
    }
  }, [isLoading, dispatch]);

  const handleAddProductToCart = (
    productData,
    qty,
    configurable_options,
    configurable_product_sku
  ) => {
    let product_data = {};
    let price = productData.price;

    if (configurable_options) {
      let configurable_item_options = [];
      for (const [key, value] of Object.entries(configurable_options)) {
        if (value !== null) {
          configurable_item_options.push({
            option_id: value.option_id.toString(),
            option_value: parseInt(value.option_value),
            option_label: value.option_label.toString(),
            option_title: value.option_title.toString(),
            price: value.price,
          });
          price = value.price;
        }
      }

      product_data = {
        id: productData.id,
        name: productData.name,
        sku: productData.sku,
        qty: qty,
        price: price,
        images: productData.images,
        product_type: "configurable",
        product_option: {
          extension_attributes: {
            configurable_item_options: configurable_item_options,
          },
        },
      };

      dispatch(setLoader(true, "Adding product to cart..."));
      dispatch(setCart(product_data));
      dispatch(fetchCartProducts());
      dispatch(setLoader(false, null));
    } else {
      product_data = {
        id: productData.id,
        name: productData.name,
        sku: productData.sku,
        qty: qty,
        images: productData.images,
        price: parseFloat(productData.price).toFixed(2),
        product_type: "simple",
      };

      dispatch(setLoader(true, "Adding product to cart..."));
      dispatch(setCart(product_data));
      dispatch(fetchCartProducts());
      dispatch(setLoader(false, null));
    }
  };

  // Transform DynamoDB product to expected format
  const transformedProduct = product ? {
    id: product.product_id,
    name: product.product_name,
    sku: product.sku,
    price: product.price,
    qty: product.quantity,
    description: product.description,
    short_description: product.short_description,
    images: product.images?.length > 0
      ? product.images.map(url => ({ url }))
      : product.image_url
        ? [{ url: product.image_url }]
        : [],
    type_id: product.product_type,
    is_giftcard: product.is_gift_card,
    gift_card_config: product.gift_card_config ? JSON.parse(product.gift_card_config) : null,
    configurable_options: product.configurable_options ? JSON.parse(product.configurable_options) : null,
    configurable_options_products: product.configurable_options_products ? JSON.parse(product.configurable_options_products) : null,
    seller_id: product.seller_id,
  } : null;

  // Transform seller to expected format
  const transformedSeller = seller ? {
    seller_id: seller.seller_id,
    name: seller.shop_title,
    shop_url: seller.shop_url,
    logo_pic: seller.logo_pic,
    supplierimage: seller.supplied_by_image,
    company_description: seller.company_description,
  } : null;

  if (productError) {
    return (
      <div id="page-product">
        <div id="product-not-exists-message">Product not found.</div>
      </div>
    );
  }

  return (
    <div id="page-product">
      {transformedProduct ? (
        <Product
          update={false}
          product={transformedProduct}
          seller={transformedSeller}
          handleProductAction={handleAddProductToCart}
        />
      ) : !loader.show ? (
        <div id="product-not-exists-message">No Product.</div>
      ) : null}
    </div>
  );
};

export default ProductPage;
